import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import {
  TextInput, SelectInput, TextareaInput, CheckboxInput, FormRow
} from '@/components/common/FormField'
import { deviceService } from '@/services/api'
import toast from 'react-hot-toast'
import { Wifi, WifiOff, Loader } from 'lucide-react'

const TYPE_OPTIONS = [
  { value: 'entry', label: 'Вход (Entry)' },
  { value: 'exit', label: 'Выход (Exit)' },
  { value: 'both', label: 'Вход и Выход' },
]

const INITIAL = {
  name: '', device_id: '', ip_address: '',
  username: 'admin', password: '', location: '',
  device_type: 'both', is_active: true, notes: '',
}


export default function DeviceModal({ open, onClose, device = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const isEdit = !!device

  useEffect(() => {
    if (open) {
      setForm(device ? {
        name: device.name || '',
        device_id: device.device_id || '',
        ip_address: device.ip_address || '',
        username: device.username || 'admin',
        password: '',  // Never prefill password
        location: device.location || '',
        device_type: device.device_type || 'both',
        is_active: device.is_active ?? true,
        notes: device.notes || '',
      } : INITIAL)

      setErrors({})
      setTestResult(null)
    }
  }, [open, device])

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: val }))
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
    if (['ip_address', 'username', 'password'].includes(key)) {
      setTestResult(null)
    }

  }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Обязательное поле'
    if (!form.device_id.trim()) e.device_id = 'Обязательное поле'
    if (!form.ip_address.trim()) e.ip_address = 'Обязательное поле'
    if (!form.location.trim()) e.location = 'Обязательное поле'
    if (!isEdit && !form.password.trim()) e.password = 'Пароль обязателен при добавлении'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleTestConnection = async () => {
    if (!form.ip_address) {
      toast.error('Укажите IP адрес')
      return
    }

    setTesting(true)
    setTestResult(null)

    // Save first to get an ID, then test (for new devices, test with temp payload)
    try {
      if (isEdit) {
        const resp = await deviceService.ping(device.id)
        setTestResult({ online: resp.data.online, info: resp.data.device_info })
        if (resp.data.online) toast.success('Терминал онлайн!')
        else toast.error('Терминал недоступен')
      } else {
        toast('Сохраните устройство, затем проверьте подключение', { icon: 'ℹ️' })
        setTestResult({ online: null, message: 'Сначала сохраните устройство' })
      }
    } catch {
      setTestResult({ online: false })
      toast.error('Ошибка подключения')
    } finally {
      setTesting(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      let savedDevice
      if (isEdit) {
        const resp = await deviceService.update(device.id, form)
        savedDevice = resp.data
        toast.success('Устройство обновлено')
      } else {
        const resp = await deviceService.create(form)
        savedDevice = resp.data
        toast.success('Устройство добавлено')

        // Auto-check connection after creation
        try {
          const pingResp = await deviceService.ping(savedDevice.id)
          if (pingResp.data.online) {
            toast.success('Подключение установлено ✓', { duration: 3000 })
          } else {
            toast('Устройство сохранено, но недоступно', { icon: '⚠️' })
          }
        } catch { /* ignore ping error */ }
      }
      qc.invalidateQueries(['devices'])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data) {
        const fieldErrors = {}
        Object.entries(data).forEach(([k, v]) => { fieldErrors[k] = Array.isArray(v) ? v[0] : v })
        setErrors(fieldErrors)
      } else {
        toast.error('Ошибка сохранения')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать терминал' : 'Добавить терминал Hikvision'}
      onSubmit={handleSubmit}
      loading={loading}
      size="lg"
    >
      {/* Connection test result banner */}
      {testResult !== null && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${
          testResult.online === true ? 'bg-green-50 text-green-700 border border-green-200' :
          testResult.online === false ? 'bg-red-50 text-red-700 border border-red-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {testResult.online === true ? <Wifi size={16} /> :
           testResult.online === false ? <WifiOff size={16} /> : null}
          {testResult.online === true ? 'Терминал доступен' :
           testResult.online === false ? 'Терминал недоступен' :
           testResult.message}
          {testResult.info?.deviceName && (
            <span className="ml-2 text-xs opacity-75">({testResult.info.deviceName})</span>
          )}
        </div>
      )}

      <FormRow>
        <TextInput
          label="Название терминала" required
          value={form.name} onChange={set('name')}
          error={errors.name} placeholder="Главный вход"
        />
        <TextInput
          label="ID терминала" required
          value={form.device_id} onChange={set('device_id')}
          error={errors.device_id} placeholder="HIKVISION-001"
          hint="Уникальный идентификатор"
        />
      </FormRow>

      <TextInput
        label="IP адрес" required
        value={form.ip_address} onChange={set('ip_address')}
        error={errors.ip_address} placeholder="192.168.1.100"
      />

      <FormRow>
        <TextInput
          label="Логин"
          value={form.username} onChange={set('username')}
          placeholder="admin"
        />
        <TextInput
          label={isEdit ? "Новый пароль (оставьте пустым чтобы не менять)" : "Пароль *"}
          type="password"
          value={form.password} onChange={set('password')}
          error={errors.password}
        />
      </FormRow>

      <TextInput
        label="Местоположение" required
        value={form.location} onChange={set('location')}
        error={errors.location}
        placeholder="Главный корпус, 1 этаж, Вход A"
      />

      <SelectInput
        label="Тип устройства"
        value={form.device_type} onChange={set('device_type')}
        options={TYPE_OPTIONS}
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-secondary flex-1"
          onClick={handleTestConnection}
          disabled={testing}
        >
          {testing ? (
            <><Loader size={14} className="animate-spin" /> Проверка...</>
          ) : (
            <><Wifi size={14} /> Проверить подключение</>
          )}
        </button>
        <CheckboxInput
          label="Активен"
          checked={form.is_active}
          onChange={set('is_active')}
        />
      </div>

      <TextareaInput
        label="Заметки"
        value={form.notes} onChange={set('notes')}
        rows={2} placeholder="Дополнительная информация..."
      />
    </ModalForm>
  )
}
